import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  Calendar,
  Lock,
  Pencil,
  Save,
  MessageSquare,
  Search,
  Download,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Send,
  X,
  Users,
  Building2,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const STATUS_LABELS = {
  leer: 'Leer',
  reserviert: 'Reserviert',
  gebucht: 'Gebucht'
};

const Logenbelegung = () => {
  const { user } = useContext(AuthContext);
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingGame, setLoadingGame] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Editing state
  const [editingLoge, setEditingLoge] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Comments dialog
  const [commentDialog, setCommentDialog] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);

  const canEdit = user?.role === 'super_admin' || user?.role === 'admin' ||
    user?.accessible_features?.some(f => f.id === 'logenbelegung' && (f.permission_level === 'edit' || f.permission_level === 'admin'));

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      const res = await axios.get('/loges/games');
      setGames(res.data);
      // Auto-select next upcoming game
      const now = new Date();
      const upcoming = res.data.find(g => new Date(g.start_date) >= now);
      if (upcoming) {
        setSelectedGameId(upcoming.id);
      } else if (res.data.length > 0) {
        setSelectedGameId(res.data[res.data.length - 1].id);
      }
    } catch (err) {
      toast.error('Fehler beim Laden der Spiele');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedGameId) {
      fetchGameData(selectedGameId);
    }
  }, [selectedGameId]);

  const fetchGameData = async (eventId) => {
    setLoadingGame(true);
    try {
      const res = await axios.get(`/loges/games/${eventId}`);
      setGameData(res.data);
      setConfig(res.data.config);
    } catch (err) {
      toast.error('Fehler beim Laden der Logendaten');
    } finally {
      setLoadingGame(false);
    }
  };

  const handleStartEdit = async (loge) => {
    if (!canEdit) return;
    if (loge.is_locked) {
      toast.error(`Loge wird von ${loge.locked_by_name} bearbeitet`);
      return;
    }

    try {
      await axios.post(`/loges/bookings/${selectedGameId}/${loge.id}/lock`);
      const booking = loge.booking || {};
      setEditingLoge(loge.id);
      setEditForm({
        status: booking.status || 'leer',
        kunde: booking.kunde || '',
        kundennummer: booking.kundennummer || '',
        anzahl_personen: booking.anzahl_personen || 0,
        catering: booking.catering || '',
        art: booking.art || '',
        hospitality_partner: booking.hospitality_partner || '',
        bearbeiter: booking.bearbeiter || '',
        ansprechpartner: booking.ansprechpartner || '',
        infos: booking.infos || '',
        catering_bestellung: booking.catering_bestellung || false,
        sz_nach_spiel: booking.sz_nach_spiel || false,
      });
    } catch (err) {
      if (err.response?.status === 423) {
        toast.error(err.response.data?.detail || 'Loge ist gesperrt');
      } else {
        toast.error('Fehler beim Sperren der Loge');
      }
    }
  };

  const handleCancelEdit = async () => {
    if (editingLoge && selectedGameId) {
      try {
        await axios.post(`/loges/bookings/${selectedGameId}/${editingLoge}/unlock`);
      } catch (err) { /* ignore */ }
    }
    setEditingLoge(null);
    setEditForm({});
  };

  const handleSaveEdit = async () => {
    if (!editingLoge || !selectedGameId) return;
    setSaving(true);
    try {
      await axios.patch(`/loges/bookings/${selectedGameId}/${editingLoge}`, editForm);
      await axios.post(`/loges/bookings/${selectedGameId}/${editingLoge}/unlock`);
      toast.success('Änderungen gespeichert');
      setEditingLoge(null);
      setEditForm({});
      fetchGameData(selectedGameId);
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenComments = async (booking) => {
    if (!booking?.id) {
      toast.error('Keine Buchung vorhanden');
      return;
    }
    setCommentDialog(booking);
    setLoadingComments(true);
    try {
      const res = await axios.get(`/loges/bookings/${booking.id}/comments`);
      setComments(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden der Kommentare');
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !commentDialog?.id) return;
    try {
      await axios.post(`/loges/bookings/${commentDialog.id}/comments`, { text: newComment.trim() });
      setNewComment('');
      const res = await axios.get(`/loges/bookings/${commentDialog.id}/comments`);
      setComments(res.data);
      toast.success('Kommentar hinzugefügt');
    } catch (err) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 2) return;
    setSearching(true);
    try {
      const res = await axios.get(`/loges/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data);
    } catch (err) {
      toast.error('Fehler bei der Suche');
    } finally {
      setSearching(false);
    }
  };

  const handleExport = async () => {
    if (!selectedGameId || !gameData) return;
    try {
      const res = await axios.get(`/loges/export/${selectedGameId}`);
      const eventTitle = res.data.event?.title || 'Export';
      const rows = res.data.data;
      if (!rows.length) { toast.error('Keine Daten zum Exportieren'); return; }
      const headers = Object.keys(rows[0]);
      const csv = [
        headers.join(';'),
        ...rows.map(r => headers.map(h => `"${(r[h] ?? '').toString().replace(/"/g, '""')}"`).join(';'))
      ].join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Logenbelegung_${eventTitle.replace(/\s+/g, '_')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export erstellt');
    } catch (err) {
      toast.error('Fehler beim Export');
    }
  };

  const handleApplyPermanent = async () => {
    if (!selectedGameId) return;
    try {
      const res = await axios.post(`/loges/games/${selectedGameId}/apply-permanent`);
      toast.success(res.data.message);
      fetchGameData(selectedGameId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Anwenden der Dauerlogen');
    }
  };

  const getStatusColor = (status) => {
    if (!config?.status_colors) {
      return status === 'gebucht' ? '#d1fae5' : status === 'reserviert' ? '#fef3c7' : '#f3f4f6';
    }
    return config.status_colors[status] || '#f3f4f6';
  };

  const getStatusBadgeVariant = (status) => {
    if (status === 'gebucht') return 'default';
    if (status === 'reserviert') return 'secondary';
    return 'outline';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="logenbelegung-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-[#e30613] mr-2" />
        <span>Lade Logenbelegung...</span>
      </div>
    );
  }

  // Game List View
  if (!selectedGameId || !gameData) {
    return (
      <div className="space-y-6" data-testid="logenbelegung-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Logenbelegung</h1>
            <p className="text-sm text-gray-500 mt-1">Verwaltung der Logenbelegung pro Spieltag</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Kunde suchen..."
                className="pl-9 w-64"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="logen-search-input"
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleSearch} disabled={searchQuery.length < 2} data-testid="logen-search-btn">
              <Search className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <Card data-testid="search-results">
            <CardHeader>
              <CardTitle className="text-base">Suchergebnisse ({searchResults.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {searchResults.map((r, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <p className="font-medium">{r.booking?.kunde || 'Unbekannt'}</p>
                    <p className="text-sm text-gray-500">
                      Loge {r.loge?.nummer} &middot; {r.event?.title} &middot; {r.event?.start_date ? format(new Date(r.event.start_date), 'dd.MM.yyyy', { locale: de }) : ''}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setSelectedGameId(r.booking?.event_id); setSearchResults([]); }}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Game List */}
        <div className="grid gap-3">
          {games.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-gray-500">Keine Heimspiele vorhanden</CardContent></Card>
          ) : (
            games.map(game => (
              <Card
                key={game.id}
                className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-[#e30613]"
                onClick={() => setSelectedGameId(game.id)}
                data-testid={`game-card-${game.id}`}
              >
                <CardContent className="py-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#e30613]/10 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-[#e30613]" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{game.title}</p>
                      <p className="text-sm text-gray-500">
                        {format(new Date(game.start_date), "EEEE, dd. MMMM yyyy 'um' HH:mm 'Uhr'", { locale: de })}
                      </p>
                      {game.location && <p className="text-xs text-gray-400 mt-0.5">{game.location}</p>}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  }

  // Game Detail / Table View
  const loges = gameData?.loges || [];
  const event = gameData?.event;

  return (
    <div className="space-y-4" data-testid="logenbelegung-detail">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { handleCancelEdit(); setSelectedGameId(null); setGameData(null); }} data-testid="back-to-games-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{event?.title}</h1>
            <p className="text-sm text-gray-500">
              {event?.start_date && format(new Date(event.start_date), "dd.MM.yyyy HH:mm", { locale: de })} Uhr
              {event?.location && ` \u2022 ${event.location}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && (
            <Button size="sm" variant="outline" onClick={handleApplyPermanent} data-testid="apply-permanent-btn">
              <Building2 className="w-4 h-4 mr-1" /> Dauerlogen anwenden
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={handleExport} data-testid="export-btn">
            <Download className="w-4 h-4 mr-1" /> CSV Export
          </Button>
          <Button size="sm" variant="ghost" onClick={() => fetchGameData(selectedGameId)} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Status Legend */}
      <div className="flex items-center gap-4 text-xs">
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded border" style={{ backgroundColor: getStatusColor(key) }} />
            <span className="text-gray-600 dark:text-gray-400">{label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 ml-2">
          <Lock className="w-3.5 h-3.5 text-orange-500" />
          <span className="text-gray-600 dark:text-gray-400">Gesperrt</span>
        </div>
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
          <span className="text-gray-600 dark:text-gray-400">Überbelegt</span>
        </div>
      </div>

      {/* Loading */}
      {loadingGame ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-5 h-5 animate-spin text-[#e30613] mr-2" />
          <span>Lade Daten...</span>
        </div>
      ) : (
        /* Table */
        <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-950" data-testid="logen-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-800 border-b text-left">
                  <th className="px-3 py-2.5 font-semibold w-16">Loge</th>
                  <th className="px-3 py-2.5 font-semibold w-20">Status</th>
                  <th className="px-3 py-2.5 font-semibold min-w-[140px]">Kunde</th>
                  <th className="px-3 py-2.5 font-semibold w-20">Kd-Nr.</th>
                  <th className="px-3 py-2.5 font-semibold w-14 text-center">Pers.</th>
                  <th className="px-3 py-2.5 font-semibold w-28">Catering</th>
                  <th className="px-3 py-2.5 font-semibold w-28">Art</th>
                  <th className="px-3 py-2.5 font-semibold w-28">Hospitality</th>
                  <th className="px-3 py-2.5 font-semibold w-28">Bearbeiter</th>
                  <th className="px-3 py-2.5 font-semibold w-28">Ansprechp.</th>
                  <th className="px-3 py-2.5 font-semibold w-12 text-center">Cat.</th>
                  <th className="px-3 py-2.5 font-semibold w-12 text-center">SZ</th>
                  <th className="px-3 py-2.5 font-semibold w-24 text-center">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {loges.map((loge) => {
                  const booking = loge.booking || {};
                  const isEditing = editingLoge === loge.id;
                  const status = isEditing ? editForm.status : (booking.status || 'leer');
                  const bgColor = getStatusColor(status);

                  return (
                    <tr
                      key={loge.id}
                      className="border-b last:border-b-0 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
                      style={{ backgroundColor: isEditing ? undefined : bgColor + '40' }}
                      data-testid={`loge-row-${loge.nummer}`}
                    >
                      {/* Loge Number */}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-gray-900 dark:text-white">{loge.nummer}</span>
                          {loge.is_mischloge && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="outline" className="text-[10px] px-1 py-0">M</Badge>
                                </TooltipTrigger>
                                <TooltipContent>Mischloge</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {loge.capacity_warning && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                                </TooltipTrigger>
                                <TooltipContent>Überbelegt! Max: {loge.kapazitaet}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {loge.is_locked && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Lock className="w-3.5 h-3.5 text-orange-500" />
                                </TooltipTrigger>
                                <TooltipContent>Gesperrt von {loge.locked_by_name}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <span className="text-[10px] text-gray-400">({loge.kapazitaet} Pl.)</span>
                      </td>

                      {/* Status */}
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Select value={editForm.status} onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}>
                            <SelectTrigger className="h-7 text-xs" data-testid="edit-status-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant={getStatusBadgeVariant(status)} className="text-xs">
                            {STATUS_LABELS[status] || status}
                          </Badge>
                        )}
                      </td>

                      {/* Kunde */}
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Input className="h-7 text-xs" value={editForm.kunde} onChange={e => setEditForm(p => ({ ...p, kunde: e.target.value }))} data-testid="edit-kunde-input" />
                        ) : (
                          <span className="font-medium">{booking.kunde || '-'}</span>
                        )}
                      </td>

                      {/* Kundennummer */}
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Input className="h-7 text-xs" value={editForm.kundennummer} onChange={e => setEditForm(p => ({ ...p, kundennummer: e.target.value }))} />
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400">{booking.kundennummer || '-'}</span>
                        )}
                      </td>

                      {/* Personen */}
                      <td className="px-3 py-2 text-center">
                        {isEditing ? (
                          <Input type="number" className="h-7 text-xs w-14 text-center" value={editForm.anzahl_personen} onChange={e => setEditForm(p => ({ ...p, anzahl_personen: parseInt(e.target.value) || 0 }))} data-testid="edit-personen-input" />
                        ) : (
                          <span className={booking.anzahl_personen > loge.kapazitaet ? 'text-red-600 font-bold' : ''}>
                            {booking.anzahl_personen || '-'}
                          </span>
                        )}
                      </td>

                      {/* Catering */}
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Select value={editForm.catering || '_none'} onValueChange={(v) => setEditForm(p => ({ ...p, catering: v === '_none' ? '' : v }))}>
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="-" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">-</SelectItem>
                              {(config?.catering_options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400 text-xs">{booking.catering || '-'}</span>
                        )}
                      </td>

                      {/* Art */}
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Select value={editForm.art || '_none'} onValueChange={(v) => setEditForm(p => ({ ...p, art: v === '_none' ? '' : v }))}>
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="-" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">-</SelectItem>
                              {(config?.art_options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400 text-xs">{booking.art || '-'}</span>
                        )}
                      </td>

                      {/* Hospitality */}
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Select value={editForm.hospitality_partner || '_none'} onValueChange={(v) => setEditForm(p => ({ ...p, hospitality_partner: v === '_none' ? '' : v }))}>
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="-" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">-</SelectItem>
                              {(config?.hospitality_options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400 text-xs">{booking.hospitality_partner || '-'}</span>
                        )}
                      </td>

                      {/* Bearbeiter */}
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Input className="h-7 text-xs" value={editForm.bearbeiter} onChange={e => setEditForm(p => ({ ...p, bearbeiter: e.target.value }))} />
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400 text-xs">{booking.bearbeiter || '-'}</span>
                        )}
                      </td>

                      {/* Ansprechpartner */}
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Input className="h-7 text-xs" value={editForm.ansprechpartner} onChange={e => setEditForm(p => ({ ...p, ansprechpartner: e.target.value }))} />
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400 text-xs">{booking.ansprechpartner || '-'}</span>
                        )}
                      </td>

                      {/* Catering Bestellung */}
                      <td className="px-3 py-2 text-center">
                        {isEditing ? (
                          <input type="checkbox" checked={editForm.catering_bestellung} onChange={e => setEditForm(p => ({ ...p, catering_bestellung: e.target.checked }))} className="rounded" />
                        ) : (
                          booking.catering_bestellung ? <CheckCircle className="w-4 h-4 text-green-600 mx-auto" /> : <span className="text-gray-300">-</span>
                        )}
                      </td>

                      {/* SZ nach Spiel */}
                      <td className="px-3 py-2 text-center">
                        {isEditing ? (
                          <input type="checkbox" checked={editForm.sz_nach_spiel} onChange={e => setEditForm(p => ({ ...p, sz_nach_spiel: e.target.checked }))} className="rounded" />
                        ) : (
                          booking.sz_nach_spiel ? <CheckCircle className="w-4 h-4 text-green-600 mx-auto" /> : <span className="text-gray-300">-</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          {isEditing ? (
                            <>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
                                <Save className="w-3.5 h-3.5 text-green-600" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleCancelEdit} data-testid="cancel-edit-btn">
                                <X className="w-3.5 h-3.5 text-red-500" />
                              </Button>
                            </>
                          ) : (
                            <>
                              {canEdit && (
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleStartEdit(loge)} disabled={loge.is_locked} data-testid={`edit-loge-${loge.nummer}-btn`}>
                                  {loge.is_locked ? <Lock className="w-3.5 h-3.5 text-orange-400" /> : <Pencil className="w-3.5 h-3.5 text-gray-500" />}
                                </Button>
                              )}
                              {booking?.id && (
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleOpenComments(booking)} data-testid={`comments-loge-${loge.nummer}-btn`}>
                                  <MessageSquare className="w-3.5 h-3.5 text-gray-500" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {loges.length === 0 && (
            <div className="text-center py-10 text-gray-500">Keine aktiven Logen für dieses Spiel</div>
          )}
        </div>
      )}

      {/* Edit Infos (shown below table when editing) */}
      {editingLoge && (
        <Card className="border-blue-200 dark:border-blue-800" data-testid="edit-infos-card">
          <CardContent className="py-3">
            <label className="text-xs font-medium text-gray-500 mb-1 block">Infos / Notizen</label>
            <Textarea
              value={editForm.infos}
              onChange={e => setEditForm(p => ({ ...p, infos: e.target.value }))}
              placeholder="Zusätzliche Informationen..."
              className="min-h-[60px] text-sm"
              data-testid="edit-infos-textarea"
            />
          </CardContent>
        </Card>
      )}

      {/* Comments Dialog */}
      <Dialog open={!!commentDialog} onOpenChange={(open) => { if (!open) { setCommentDialog(null); setComments([]); setNewComment(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Kommentare - Loge {commentDialog?.loge_id}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-64">
            <div className="space-y-3 pr-3">
              {loadingComments ? (
                <p className="text-sm text-gray-500 text-center py-4">Lade...</p>
              ) : comments.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">Noch keine Kommentare</p>
              ) : (
                comments.map(c => (
                  <div key={c.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3" data-testid={`comment-${c.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{c.user_name}</span>
                      <span className="text-[10px] text-gray-400">
                        {format(new Date(c.created_at), 'dd.MM.yy HH:mm', { locale: de })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{c.text}</p>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
          {canEdit && (
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Kommentar schreiben..."
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddComment()}
                data-testid="new-comment-input"
              />
              <Button size="sm" onClick={handleAddComment} disabled={!newComment.trim()} data-testid="send-comment-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Logenbelegung;
